import React from "react"

import { Input, Switch } from "@coval/ui"

import { FieldLabel } from "@/components/shared/forms/FieldLabel"

import { WEBSOCKET_TOOLTIPS } from "./tooltips"
import type { WebSocketFieldProps } from "./types"

interface HandshakeFieldsProps extends WebSocketFieldProps {
  isHttpFirst: boolean
}

export const HandshakeFields: React.FC<HandshakeFieldsProps> = ({
  metadata,
  onMetadataUpdate,
  disabled = false,
  isHttpFirst,
}) => {
  return (
    <div className="space-y-4">
      <h4 className="text-sm font-medium text-coval-text">Handshake</h4>

      <div className="space-y-2">
        <FieldLabel
          htmlFor="ready-message-type"
          tooltip={WEBSOCKET_TOOLTIPS.readyMessageType}
        >
          Ready Message Type
        </FieldLabel>
        <Input
          id="ready-message-type"
          aria-label="Ready Message Type"
          placeholder="session_ready"
          value={metadata.handshake_ready_message_type || ""}
          onChange={(e) =>
            onMetadataUpdate({
              handshake_ready_message_type: e.target.value,
            })
          }
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          {isHttpFirst
            ? 'Message type indicating the session is ready. Leave empty to skip handshake waiting (default: "session_ready")'
            : 'Message type indicating the session is ready (default: "session_ready")'}
        </p>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id="require-session-id"
          checked={metadata.handshake_requires_session_id !== false}
          onCheckedChange={(checked) =>
            onMetadataUpdate({ handshake_requires_session_id: checked })
          }
          disabled={disabled}
        />
        <div className="flex flex-col gap-1">
          <FieldLabel
            htmlFor="require-session-id"
            tooltip={WEBSOCKET_TOOLTIPS.requireSessionId}
          >
            Require Session ID
          </FieldLabel>
          <p className="text-xs text-coval-text-muted">
            Whether the handshake needs a session ID (default: enabled)
          </p>
        </div>
      </div>

      <div className="space-y-2">
        <FieldLabel
          htmlFor="handshake-timeout"
          tooltip={WEBSOCKET_TOOLTIPS.handshakeTimeout}
        >
          Handshake Timeout (seconds)
        </FieldLabel>
        <Input
          id="handshake-timeout"
          aria-label="Handshake Timeout"
          type="number"
          placeholder="30"
          min={1}
          value={
            metadata.handshake_timeout_seconds != null
              ? String(metadata.handshake_timeout_seconds)
              : ""
          }
          onChange={(e) => {
            const raw = e.target.value
            if (raw === "") {
              onMetadataUpdate({
                handshake_timeout_seconds: undefined,
              })
            } else {
              const parsed = parseInt(raw, 10)
              if (!isNaN(parsed)) {
                onMetadataUpdate({
                  handshake_timeout_seconds: parsed,
                })
              }
            }
          }}
          disabled={disabled}
        />
        <p className="text-xs text-coval-text-muted">
          Seconds to wait for the handshake to complete (default: 30)
        </p>
      </div>
    </div>
  )
}
